import React from 'react';
import { Eye } from 'lucide-react';

// Define the default task field settings
export const defaultFieldSettings = {
  description: true,
  tags: true,
  horizon: true,
  recurrence: false,
  dueDate: true,
  notes: true,
  parentTask: true,
  assignedTo: true,
  estimatedTime: false,
};

interface TaskFieldSettingsProps {
  taskFieldSettings: typeof defaultFieldSettings;
  setTaskFieldSettings: React.Dispatch<React.SetStateAction<typeof defaultFieldSettings>>;
  isLoading: boolean;
}

export const TaskFieldSettings: React.FC<TaskFieldSettingsProps> = ({
  taskFieldSettings,
  setTaskFieldSettings,
  isLoading,
}) => {
  const toggleAllFields = (checked: boolean) => {
    setTaskFieldSettings(prev => 
      Object.keys(prev).reduce((acc, key) => ({
        ...acc,
        [key]: checked
      }), {} as typeof defaultFieldSettings)
    );
  };

  const toggleField = (field: keyof typeof defaultFieldSettings) => {
    setTaskFieldSettings(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const allFieldsChecked = Object.values(taskFieldSettings).every(Boolean);
  const someFieldsChecked = Object.values(taskFieldSettings).some(Boolean);

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        <Eye className="w-4 h-4 inline mr-2" />
        Task Form Fields
      </label>
      <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md p-3 bg-gray-50 dark:bg-gray-800">
        {/* Select All/None Toggle */}
        <div className="flex items-center space-x-2 pb-2 border-b border-gray-300 dark:border-gray-600">
          <input
            type="checkbox"
            id="toggleAll"
            checked={allFieldsChecked}
            ref={(el) => {
              if (el) el.indeterminate = someFieldsChecked && !allFieldsChecked;
            }}
            onChange={(e) => toggleAllFields(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            disabled={isLoading}
          />
          <label htmlFor="toggleAll" className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {allFieldsChecked ? 'Unselect All' : 'Select All'}
          </label>
        </div>
        
        {/* Individual Field Toggles */}
        {[
          { key: 'description', label: 'Description', desc: 'Detailed task description' },
          { key: 'tags', label: 'Tags', desc: 'Task categorization tags' },
          { key: 'horizon', label: 'Horizon', desc: 'Task horizon (Now/Next/Soon/Later/Someday)' },
          { key: 'recurrence', label: 'Recurrence', desc: 'Recurring task settings' },
          { key: 'dueDate', label: 'Due Date', desc: 'Task deadline' },
          { key: 'notes', label: 'Notes', desc: 'Additional notes (shown when editing)' },
          { key: 'parentTask', label: 'Parent Task', desc: 'Subtask relationship' },
          { key: 'assignedTo', label: 'Assigned To', desc: 'Team member assignment (team mode only)' },
          { key: 'estimatedTime', label: 'Estimated Time', desc: 'Expected time to complete' },
        ].map(({ key, label, desc }) => (
          <div key={key} className="flex items-start space-x-2">
            <input
              type="checkbox"
              id={key}
              checked={taskFieldSettings[key as keyof typeof defaultFieldSettings]}
              onChange={() => toggleField(key as keyof typeof defaultFieldSettings)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
              disabled={isLoading}
            />
            <div className="flex-1">
              <label htmlFor={key} className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                {label}
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {desc}
              </p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        Choose which fields to show in the task form. Title is always visible.
      </p>
    </div>
  );
};